<script setup lang="ts">
import { ref, reactive, onBeforeMount } from "vue"
import { useGetDocs } from "@/mixins/modularFirebaseOperations"
import { getAllUsers } from "@/composables/userComposables"
import Multiselect from "vue-multiselect"
import StarPicker from "@/components/reusable/starPicker.vue"
import type { AcPotential } from "@/types/crm"
import type { ColorMetaField } from "@/types/metas"
import type { fetchedUser } from "@/types/fetchedUser"

/* 
TODO: 

- Add referrer and third party refferers
- Add potential categories

*/

// No props needed for add-only form

const emit = defineEmits(["potential-added", "cancel"])

const potentialName = ref("")
const potentialType = ref<"company" | "individual" | null>(null)
const potentialRating = ref(0)
const potentialDescription = ref("")
const selectedPotentialCategories = ref<ColorMetaField[]>([])
const referredBy = ref<fetchedUser | null>(null)
const thirdPartyRefferer = ref("")
const { docs, isLoadingDocs } = useGetDocs("crm/jobCategories/list")
const allUsers = ref<fetchedUser[]>([])
const isLoadingUsers = ref(false)

const contact = reactive({
    title: "Genel",
    emailAddress: "",
    phoneNumber: "",
    address: "",
    website: "",
})

const savePotential = async () => {
    // Validate required fields
    if (!potentialName.value.trim()) {
        alert('Lütfen fırsat adını giriniz.')
        return
    }
    
    if (!referredBy.value) {
        alert('Lütfen keşfeden kullanıcıyı seçiniz.')
        return
    }
    
    if (!potentialType.value) {
        alert('Lütfen fırsat türünü seçiniz.')
        return
    }

    const newPotential: Partial<AcPotential> = {
        title: potentialName.value,
        type: potentialType.value,
        potentialRating: potentialRating.value,
        description: potentialDescription.value,
        contact: [contact],
        potentialCategories: selectedPotentialCategories.value,
        referredBy: referredBy.value,
        thirdPartyRefferrer: thirdPartyRefferer.value,
    }

    emit("potential-added", newPotential)
}

onBeforeMount( async () => {
    await getAllUsers(allUsers.value, isLoadingUsers)
} )

</script>

<template>
    <form @submit.prevent="savePotential" class="ac-main-form !border-none">
        <header class="flex justify-between w-full">
            <h2 class="subtitle mb-8">Fırsat Bilgileri</h2>
            <star-picker @update:picked-quantity="potentialRating = $event" v-model="potentialRating" :maxQuantity="5" :pickedQuantity="potentialRating" />
        </header>
        <div class="flex gap-4"> 
            <div class="ac-form-control w-full">
                <label class="mb-4" for="potentialName">Fırsat Adı</label>
                <input v-model="potentialName" type="text" required class="w-full title-field p-3" id="potentialName">
        </div>

        <div class="ac-form-control w-full">
            <label class="mb-4" for="potentialType">Fırsat Tipi</label>
            <select v-model="potentialType" required class="w-full !py-3">
                <option disabled :value="null">Lütfen Bir Fırsat Türü Seçin</option>
                <option value="company">Şirket</option>
                <option value="individual">Bireysel</option>
            </select>
        </div>
        </div>

        <div class="ac-form-control w-full">
            <label class="mb-4" for="potentialCategories">Fırsat Kategorileri</label>
            <multiselect class="w-full border border-[var(--accent-gray-subtle)] py-4 pl-3 border-gray-200" 
                v-model="selectedPotentialCategories" 
                :options="docs"
                multiple
                :placeholder="isLoadingDocs ? 'İş Kategorileri Yükleniyor...' : 'Lütfen Bir İş Kategorisi Seçin'"
                track-by="id"
                select-label="Seçmek için tıklayın"
                deselect-label="Seçimi kaldırmak için tıklayın"
                selected-label="Seçildi"
                label="name"
            >
            <template #tag="{ option, remove }">
                <div :style="{backgroundColor: option.color}" class="custom-option">
                    <span >{{ option.name }}</span>
                    <span class="remove-option" @click="remove(option)">x</span>
                </div>
            </template>
        </multiselect>
        </div>

        <div class="ac-form-control">
            <label class="mb-4" for="potentialRating">Fırsat Açıklaması</label>
            <textarea placeholder="Buraya fırsatın detayları hakkında kısa bir bilgi yazabilirsiniz." v-model="potentialDescription" class="w-full p-3 h-32 resize-none"></textarea>
        </div>

        <div class="ac-form-control">
           
            <div class="date-container flex w-full gap-4">
                <div class="w-1/3">
                    <h3 class="third-title ">E-Posta</h3>
                    <input placeholder="ad.soyad@gmail.com" v-model="contact.emailAddress" type="email" class="w-full p-3">
                </div>
                <div class="w-1/3">
                    <h3 class="third-title ">Telefon</h3>
                    <input placeholder="0555 555 55 55" v-model="contact.phoneNumber" type="tel" class="w-full p-3">
                </div>
                <div class="w-1/3">
                    <h3 class="third-title ">Web Sitesi</h3>
                    <input placeholder="https://www.sirketsitesi.com" v-model="contact.website" type="url" class="w-full p-3">
                </div>
            </div>
        </div>

        <div class="ac-form-control">
            <div class="reference-container w-full flex gap-4   ">
                <div class="w-full flex-col flex">
                    <label class="mb-4" for="potentialReferrer">Keşfeden</label>
                    <select v-model="referredBy" required class="w-full !py-3">
                        <option disabled :value="null">Lütfen Bir Kullanıcı Seçin</option>
                        <option v-for="user in allUsers" :key="user.id" :value="user">{{ user.userName }}</option>
                    </select>
                </div>
                <div class="w-full flex-col flex">
                    <label class="mb-4" for="potentialReferrer">Aktaran Üçüncü Taraf</label>
                    <input placeholder="Aktaran" v-model="thirdPartyRefferer" type="text">
                </div>
            </div>
            
        </div>


        <div class="flex items-center justify-center gap-6 w-full">
            <button type="submit" class="ac-primary-button text-2xl mt-8">
                Fırsat Oluştur
            </button>
            <button type="button" @click="$emit('cancel')" class="ac-secondary-button text-2xl mt-8">İptal</button>
        </div>
    </form>
</template>

<style scoped>
.custom-option {
  padding: 5px 10px;
  margin-right: 5px;
  margin-bottom: 1rem;
  border-radius: 5px;
  color: white !important;
  display: inline-block;
}

.remove-option {
  margin-left: 10px;
  cursor: pointer;
}

.multiselect__tags{
    border: none;
}

.email-multiselect .multiselect__option--highlight::after{
    background-color: #c4c4c4;
}
.email-multiselect .multiselect__option--highlight.multiselect__option{
    background-color: #c4c4c4;
}
</style>